import { Capacitor } from '@capacitor/core';
import { CapacitorCalendar } from '@ebarooni/capacitor-calendar';
import type { Flight } from '../types';

const MAX_CALENDAR_RESULTS = 25;

export interface CalendarFlight {
  title: string;
  flightNumber: string;
  date: string;
  departureAirport: string;
  arrivalAirport: string;
  departureTime?: string;
  arrivalTime?: string;
  airlineCode: string;
}

/** Request full calendar access. Returns true if granted. */
export async function requestCalendarAccess(): Promise<boolean> {
  if (!Capacitor.isNativePlatform()) return false;

  try {
    const result = await CapacitorCalendar.requestFullCalendarAccess();
    return result.result === 'granted';
  } catch (err) {
    console.warn('[calendarImport] permission request failed:', err);
    return false;
  }
}

/** Check if calendar permission is already granted */
export async function hasCalendarAccess(): Promise<boolean> {
  if (!Capacitor.isNativePlatform()) return false;

  try {
    const { result: perms } = await CapacitorCalendar.checkAllPermissions();
    return perms.readCalendar === 'granted';
  } catch {
    return false;
  }
}

/**
 * Read calendar events for a given month and extract flight-like entries.
 * Looks for patterns: flight numbers (LH 400, EK201), IATA codes (FRA, JFK),
 * route formats (FRA-JFK, FRA→JFK).
 */
export async function importFlightsFromCalendar(
  month: number,
  year: number,
  userAirlineCode: string
): Promise<CalendarFlight[]> {
  // Build date range for the month
  const from = new Date(year, month - 1, 1);
  const to = new Date(year, month, 0, 23, 59, 59); // last day of month

  const { result: events } = await CapacitorCalendar.listEventsInRange({
    from: from.getTime(),
    to: to.getTime(),
  });

  const flights: CalendarFlight[] = [];
  const seen = new Set<string>();

  for (const event of events) {
    const parsed = parseCalendarEvent(
      event.title ?? '',
      event.location ?? '',
      event.description ?? '',
      event.startDate,
      event.endDate,
      userAirlineCode
    );
    if (!parsed) continue;

    // Dedup by flight number + date + route
    const key = `${parsed.flightNumber}|${parsed.date}|${parsed.departureAirport}|${parsed.arrivalAirport}`;
    if (seen.has(key)) continue;
    seen.add(key);

    flights.push(parsed);
  }

  // Sort by date then time
  flights.sort((a, b) => {
    const dateCompare = a.date.localeCompare(b.date);
    if (dateCompare !== 0) return dateCompare;
    return (a.departureTime ?? '').localeCompare(b.departureTime ?? '');
  });

  return flights.slice(0, MAX_CALENDAR_RESULTS);
}

// ── Parsing helpers ──

const FLIGHT_NUMBER_RE = /\b([A-Z]{2})\s?(\d{1,4})\b/;
const IATA_CODE_RE = /\b([A-Z]{3})\b/g;
const ROUTE_RE = /\b([A-Z]{3})\s*[-–→>]\s*([A-Z]{3})\b/;
function parseCalendarEvent(
  title: string,
  location: string,
  description: string,
  startDate: number,
  endDate: number,
  defaultAirline: string
): CalendarFlight | null {
  // Combine all text for analysis
  const allText = `${title} ${location} ${description}`.toUpperCase();

  // Must have a flight number pattern
  const flightMatch = allText.match(FLIGHT_NUMBER_RE);
  if (!flightMatch) return null;

  const airlineCode = flightMatch[1];
  const flightNumber = `${flightMatch[1]} ${flightMatch[2]}`;

  // Try to extract route (DEP-ARR)
  let dep = '';
  let arr = '';

  const routeMatch = allText.match(ROUTE_RE);
  if (routeMatch) {
    dep = routeMatch[1];
    arr = routeMatch[2];
  } else {
    // Look for standalone IATA codes (3 uppercase letters that look like airports)
    const codes = [...allText.matchAll(IATA_CODE_RE)]
      .map((m) => m[1])
      .filter((code) => isLikelyAirport(code) && code !== airlineCode);

    if (codes.length >= 2) {
      dep = codes[0];
      arr = codes[1];
    }
  }

  // Need at least the flight number to be useful
  if (!dep && !arr) return null;

  // Extract date from start timestamp
  const start = new Date(startDate);
  const date = `${start.getFullYear()}-${String(start.getMonth() + 1).padStart(2, '0')}-${String(start.getDate()).padStart(2, '0')}`;

  // Extract times
  const depTime = formatTime(start);
  const end = new Date(endDate);
  const arrTime = formatTime(end);

  return {
    title: title.trim(),
    flightNumber,
    date,
    departureAirport: dep,
    arrivalAirport: arr,
    departureTime: depTime !== '00:00' ? depTime : undefined,
    arrivalTime: arrTime !== '00:00' ? arrTime : undefined,
    airlineCode: airlineCode || defaultAirline,
  };
}

function formatTime(d: Date): string {
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}

/** Quick heuristic — filter out common 3-letter non-airport strings */
const NON_AIRPORTS = new Set([
  'THE', 'AND', 'FOR', 'NOT', 'ALL', 'ARE', 'BUT', 'HAS', 'HIS', 'HER',
  'HOW', 'ITS', 'LET', 'MAY', 'NEW', 'NOW', 'OLD', 'OUR', 'OUT', 'OWN',
  'SAY', 'SHE', 'TOO', 'USE', 'DAY', 'OFF', 'SBY', 'MON', 'TUE', 'WED',
  'THU', 'FRI', 'SAT', 'SUN', 'JAN', 'FEB', 'MAR', 'APR', 'JUN', 'JUL',
  'AUG', 'SEP', 'OCT', 'NOV', 'DEC', 'EST', 'UTC', 'GMT', 'CET',
]);

function isLikelyAirport(code: string): boolean {
  return code.length === 3 && /^[A-Z]{3}$/.test(code) && !NON_AIRPORTS.has(code);
}

/** Convert CalendarFlight[] to Flight[] for saving */
export function calendarFlightsToFlights(
  calendarFlights: CalendarFlight[],
  userId: string,
  selectedIndices: Set<number>
): Flight[] {
  return calendarFlights
    .filter((_, i) => selectedIndices.has(i))
    .map((cf, i) => ({
      id: `cal-${Date.now()}-${i}`,
      userId,
      flightNumber: cf.flightNumber,
      date: cf.date,
      departureAirport: cf.departureAirport,
      arrivalAirport: cf.arrivalAirport,
      departureTime: cf.departureTime,
      arrivalTime: cf.arrivalTime,
      airlineCode: cf.airlineCode,
      source: 'calendar_import' as const,
    }));
}
